import { Box, Button, Flex, Spinner, useDisclosure, useToast } from "@chakra-ui/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useContext, useEffect, useState } from "react";
import {
  GET_REQUEST_STATUS,
  GET_TERMINATE_STATUS,
} from "../../../apis/constants.api";
import {
  instanceLaunch,
  getUserInstanceStatus,
  terminateInstance,
  getTerminateStatus,
  activeSession,
} from "../../../apis/vmRequests.api";
import { AppContext } from "../../../contexts/AppContext";
import { isAdmin, isSysAdmin } from "../../../utils/users.utils";
import OpenTerminal from "./components/OpenTerminal";
import { useSocket } from "../../Socket";
import { STATUS } from "./constant";
import Listing from "./components/listing";
import OsOptionModal from "./components/OsOptionModal";
import { useDispatch, useSelector } from "react-redux";
import { setTerminalType } from "../../../store/terminalSlice";

const VMRequests = () => {
  const [stateApp] = useContext(AppContext);
  const [sessionResponse, setSessionResponse] = useState(null);
  const { user } = stateApp;
  const [sessionStarted, setSessionStarted] = useState(false);
  const { socket } = useSocket();
  const [instanceId, setInstanceId] = useState(null);
  const [isInitializing, setIsInitializing] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const dispatch = useDispatch();
  const terminalType = useSelector((state) => state.terminal.terminalType);

  const { data: status, refetch: refetchStatus } = useQuery({
    queryKey: [GET_REQUEST_STATUS],
    queryFn: getUserInstanceStatus,
    enabled: true, // auto-fetch
  });

  const { data: terminateStatus, refetch: refetchTerminateStatus } = useQuery({
    queryKey: [GET_TERMINATE_STATUS],
    queryFn: getTerminateStatus,
    enabled: true, // auto-fetch
  });

  useEffect(() => {
    refetchStatus();
    refetchTerminateStatus();
  }, []);

  const toast = useToast();

  const { mutate: requestInstance, isLoading: instanceLoad } = useMutation({
    mutationFn: (data) => {
      return instanceLaunch(data);
    },
    onSuccess: (data) => {
      refetchStatus();
      const instanceId = data?.data?.instanceId;
      onClose();
      setInstanceId(instanceId);
      if (instanceId) {
        socket.emit("START_SSM", { instanceId: instanceId });
        setSessionStarted(true);
      }
      if (data.data.status === "INSTANCE_LIMIT_REACHED") {
        setWarningMessage(data.data.message); // display it in the UI
      }
      else if (data.data.message) {
        toast({
          title: "Success",
          description: data.data.message,
          status: "success",
          duration: 2000,
          isClosable: true,
          position: "top-right",
        });
      }
    },
  });

  const { mutate: refetchSession, isloading: activeLoading } = useMutation({
    mutationFn: (data) => {
      return activeSession();
    },
    onSuccess: (data) => {
      refetchStatus();
      setIsInitializing(true);

      const instanceId = data?.data?.instanceId;
      setInstanceId(instanceId);
      if (instanceId) {
        if (socket && socket.connected) {

          socket.emit("START_SSM", { instanceId: instanceId });
          setSessionStarted(true);
        }
        else {
          console.warn("Socket NOT connected. Waiting to emit START_SSM...");
          socket?.once("connect", () => {
            socket.emit("START_SSM", { instanceId });
            setSessionStarted(true);
          });
          socket?.connect(); // trigger reconnect if using manual connect
        }
      }
    },
  });

  const { mutate: terminate } = useMutation({
    mutationFn: (data) => {
      return terminateInstance(data);
    },
    onSuccess: (data) => {
      refetchTerminateStatus();

      if (data.data.message) {
        toast({
          title: "Success",
          description: data.data.message,
          status: "success",
          duration: 2000,
          isClosable: true,
          position: "top-right",
        });
      }
      setSessionResponse(null);
    },
  });

  const requests = (formData) => {
    dispatch(setTerminalType({ terminalType: formData.os }));
    requestInstance(formData.os);

  };

  const directRequest = () => {
    if (status?.data.status === STATUS.Approved) {
      requestInstance(terminalType);
    } else {
      onOpen();
    }

  };


  const handleTerminate = () => {
    terminate(instanceId);
    setInstanceId(null);
  };

  useEffect(() => {
    if (!sessionStarted) return;
    socket?.on?.("ssm-data", (data) => {
      setSessionResponse(data);
      setIsInitializing(() => false);
    });

    return () => {
      socket?.disconnect();
      setSessionStarted(false);
    };
  }, [sessionStarted]);

  useEffect(() => {
    if (warningMessage) {
      const timer = setTimeout(() => {
        setWarningMessage("");
      }, 20000); // 20 seconds
      return () => clearTimeout(timer);
    }
  }, [warningMessage]);

  useEffect(() => {
    setTimeout(() => {
      refetchSession();
    }, 3000);
    // call the API on component mount (or page refresh)
  }, []);

  return (
    <>
      <Flex
        dir="column"
        flex="1 1 auto"
        padding={{ base: "15px", lg: "25px", xl: "35px" }}
        background="#F7F7F7"
        height="100%"
      >
        <Box w="100%" background="white" borderRadius="br.10" padding="25px">
          <Flex
            width="100%"
            alignItems="center"
            justifyContent="space-between"
            gap={{ md: "10px", lg: "0" }}
          >
            {(([STATUS.Approved, null].includes(status?.data.status) ||
              [STATUS.Terminate].includes(
                terminateStatus?.data.status
              ))) && (
                <Box pr="20px">
                  {sessionResponse ? (
                    <Button
                      onClick={handleTerminate}
                      px={{ md: "15px", lg: "30px" }}
                      py="8px"
                      border="1px solid #713EDD"
                      fontSize="fs.16"
                      fontWeight="fw.500"
                      background="linear-gradient(134.59deg, #713EDD -1.98%, #A17DEE 111.17%)"
                      _hover={{
                        background:
                          "linear-gradient(134.59deg, #A17DEE -1.98% ,#713EDD 111.17%)",
                      }}
                      color="#FFFFFF"
                    >
                      Stop VM
                    </Button>
                  ) : (
                    <Button
                      onClick={directRequest}
                      px={{ md: "15px", lg: "30px" }}
                      py="8px"
                      border="1px solid #713EDD"
                      fontSize="fs.16"
                      fontWeight="fw.500"
                      background="linear-gradient(134.59deg, #713EDD -1.98%, #A17DEE 111.17%)"
                      _hover={{
                        background:
                          "linear-gradient(134.59deg, #A17DEE -1.98% ,#713EDD 111.17%)",
                      }}
                      color="#FFFFFF"
                    >
                      Start VM
                    </Button>
                  )}
                </Box>
              )}
          </Flex>
          {/* )} */}
          {(!isSysAdmin(user) && !isAdmin(user)) &&
            [STATUS.Rejected, STATUS.Pending].includes(status?.data.status) && (
              <Flex>
                <Box
                  mt="20px"
                  pr="20px"
                  py="15px"
                  px="16px"
                  background="#E5E5FE"
                  borderRadius="8px"
                  border="1px solid #E0E0E0"
                  color="#333333"
                  fontSize="fs.14"
                  fontWeight="fw.400"
                  width="100%"
                >
                  {status.data.message}
                </Box>
              </Flex>
            )}

          {
            warningMessage && (
              <Flex>
                <Box
                  mt="20px"
                  pr="20px"
                  py="15px"
                  px="16px"
                  background="#FEF3C7"         // Light yellow background
                  borderRadius="8px"
                  border="1px solid #FCD34D"    // Yellow border
                  color="#92400E"               // Dark amber text
                  fontSize="14px"
                  fontWeight="400"
                  width="100%"
                >
                  ⚠️ {warningMessage}
                </Box>
              </Flex>
            )
          }

          {instanceLoad && [STATUS.Approved].includes(status?.data.status) && (
            <Flex>
              <Box
                mt="20px"
                pr="20px"
                py="15px"
                px="16px"
                background="#E5E5FE"
                borderRadius="8px"
                border="1px solid #E0E0E0"
                color="#333333"
                fontSize="fs.14"
                fontWeight="fw.400"
                width="100%"
              >
                Please wait, your terminal is being intialized
              </Box>
              <Flex className="overlay-loader" pos="absolute" top="50%" left="50%" zIndex="100" bg={'whiteAlpha.300'}>
                <Spinner
                  thickness='4px'
                  speed='0.65s'
                  emptyColor='gray.200'
                  color='purple.500'
                  size='xl'
                />
              </Flex>
            </Flex>
          )}

          {
            activeLoading && (
              <Flex className="overlay-loader" pos="absolute" top="50%" left="50%" zIndex="100" bg={'whiteAlpha.300'}>
                <Spinner
                  thickness='4px'
                  speed='0.65s'
                  emptyColor='gray.200'
                  color='purple.500'
                  size='xl'
                />
              </Flex>
            )
          }

          {["approved"].includes(status?.data.status) && (
            <Flex>
              <Box
                mt="20px"
                pr="20px"
                py="15px"
                px="16px"
                background="#E5E5FE"
                borderRadius="8px"
                border="1px solid #E0E0E0"
                color="#333333"
                fontSize="fs.14"
                fontWeight="fw.400"
                width="100%"
              >
                Your request has been approved. You may now start your VM
                instance.
              </Box>
            </Flex>
          )}
          {
            sessionResponse && <OpenTerminal />
          }
          {isSysAdmin(user) && (
            <Listing />
          )}
        </Box>
        {/* requests, loader,  */}
        {
          isOpen && [STATUS.Approved, null].includes(status?.data.status) && (
            <OsOptionModal requests={requests} onClose={onClose} isLoading={instanceLoad} />
          )
        }
      </Flex>

    </>
  );
};

export default VMRequests;
